import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import IncidentCard from '@components/incidentCard/IncidentCard'

import SectionTitle from '@components/sectionTitle/SectionTitle'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { isBefore } from 'date-fns'
import { isPrestaIncidentInProgress, isPrestaIncidentPaused } from '@utils/incidentUtils'

interface Props {
  incidents: IncidentResponse[]
  onSeeAll: () => void
}

interface ComponentProps {
  sections: IncidentSection[]
  renderIncident: (incident: IncidentResponse) => React.ReactNode
}

interface IncidentSection {
  label: string
  incidents: IncidentResponse[]
  value: string
}

const IncidentListSectionWeb = ({ sections, renderIncident }: ComponentProps) => {
  const i18n = useI18n()

  const renderSection = (section: IncidentSection) => (
    <SectionContainer key={section.label}>
      <SectionLabel>{`${section.label} (${section.incidents.length})`}</SectionLabel>
      {section.incidents.length > 0 ? (
        <IncidentRow>{section.incidents.map(renderIncident)}</IncidentRow>
      ) : (
        <MissingInfo>{i18n.t('components.incidentListSection.noIncident')}</MissingInfo>
      )}
    </SectionContainer>
  )

  return <>{sections.map(renderSection)}</>
}

const IncidentListSectionMobile = ({ sections, renderIncident }: ComponentProps) => {
  const i18n = useI18n()

  const tabs: { label: string; value: string }[] = sections.map(({ label, value, incidents }) => ({
    label: label + ` (${incidents.length})`,
    value,
  }))

  const [tab, setTab] = React.useState(tabs[0])

  const displayedIncidents = React.useMemo(() => {
    const section = sections.find((section) => section.value === tab.value)
    return section ? section.incidents : []
  }, [sections, tab])

  return (
    <>
      <SegmentedContainer>
        <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
      </SegmentedContainer>

      {displayedIncidents.length > 0 ? (
        displayedIncidents.map(renderIncident)
      ) : (
        <MissingInfo>{i18n.t('components.incidentListSection.noIncident')}</MissingInfo>
      )}
    </>
  )
}

const IncidentListSection = ({ incidents, onSeeAll }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const renderIncident = (incident: IncidentResponse) => (
    <IncidentContainer
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${incident.id}`, { incidentId: incident.id })}
      key={incident.id}>
      <IncidentCard cardHeight={cardHeight} setCardHeight={assignCardHeight} incident={incident} />
    </IncidentContainer>
  )

  const [cardHeight, setCardHeight] = React.useState(0)

  const sections: IncidentSection[] = React.useMemo(() => {
    const orderedByStatus = incidents.reduce(
      (acc, cur) => {
        if (isPrestaIncidentInProgress(cur.status)) {
          acc[0].incidents.push(cur)
        } else if (isPrestaIncidentPaused(cur.status)) {
          acc[1].incidents.push(cur)
        }

        return acc
      },
      [
        { label: i18n.t('components.prestaIncidentListSection.inProgress'), value: 'inProgresss', incidents: [] },
        { label: i18n.t('components.prestaIncidentListSection.paused'), value: 'paused', incidents: [] },
      ] as IncidentSection[]
    )

    return orderedByStatus.map((section) => ({
      ...section,
      incidents: section.incidents.sort((a, b) => {
        const sortResult = isBefore(new Date(a.dateDeCreation), new Date(b.dateDeCreation)) ? 1 : -1

        return sortResult
      }),
    }))
  }, [incidents, i18n])

  const assignCardHeight = (height: number) => {
    if (height > cardHeight) {
      setCardHeight(height)
    }
  }

  return (
    <>
      <SectionTitle
        title={i18n.t('components.orgaIncidentListSection.incidents')}
        button={{
          label: i18n.t(`components.orgaIncidentListSection.${isPhone ? 'seeAll' : 'seeAllIncidents'}`),
          onClick: onSeeAll,
        }}
      />
      {isPhone ? (
        <IncidentListSectionMobile sections={sections} renderIncident={renderIncident} />
      ) : (
        <IncidentListSectionWeb sections={sections} renderIncident={renderIncident} />
      )}
    </>
  )
}

export default IncidentListSection

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
const SectionContainer = styled(View)`
  padding: 16px 0;
`
const SectionLabel = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
`
const IncidentRow = styled(View)`
  flex-flow: row wrap;
`
const IncidentContainer = styled(TouchableOpacity)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '4px 0px;' : '0px 16px 16px 0px')};
`
const SegmentedContainer = styled(View)`
  margin: 10px 0 16px;
`
